<template>
  <div v-if="!specimenData" class="loading">Loading...</div>
  <div v-else-if="!specimenData.catalog_number" class="loading">No Data</div>
  <div v-else class="min-h-screen">
    <div
      class="
        pagination-bg pagination-bg-sm pagination-bg-lg pagination-bg-2xl
        bg-center
      "
    >
      <div class="max-w-screen-2xl mx-auto pt-16 sm:pt-20">
        <div class="flex justify-center">
          <img src="@/assets/search-result.svg" class="sm:w-16 sm:h-16" alt="標本資訊"/>
        </div>
        <h1 class="text-3xl sm:text-4xl font-black mt-5">標本資訊</h1>
      </div>
    </div>
    <div id="scroll-position" class="h-12" /> <!-- for scrolling position -->
    <div class="max-w-screen-2xl mx-auto pb-20 px-5 sm:px-10">
      <div class="lg:flex lg:justify-between mb-16">
        <div class="flex space-x-5 justify-start">
          <div class="font-bold text-lg text-left flex-none">
            <router-link to="/">首頁</router-link>
          </div>
          <p class="text-lg">></p>
          <div class="text-lg font-bold text-left flex-none">
            <!-- 回搜尋結果頁 -->
            <a class="cursor-pointer" @click="backToSearchResultPage"
              >搜尋結果</a
            >
          </div>
          <p class="text-lg">></p>
          <p class="text-lg text-color-blue font-bold text-left truncate">
            標本資訊
          </p>
        </div>
        <!-- 開啟意見回饋表單按鈕(1024px以上顯示) -->
        <button
          @click="feedbackShow = true"
          class="feedback-button hidden lg:block"
        >
          意見回饋
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div class="flex flex-col h-full">
          <div class="mb-5 flex justify-between items-center flex-none">
            <h2 class="text-2xl text-left font-black">標本影像</h2>
            <div class="flex space-x-5 lg:space-x-0">
              <!-- 下載縮圖按鈕 -->
              <button
                v-if="specimenData.state_of_check"
                @click="downloadImage(specimenData.id)"
              >
                <img src="@/assets/download.svg" alt="下載標本"/>
              </button>
              <!-- 開啟意見回饋表單按鈕(1024px以下顯示) -->
              <button @click="feedbackShow = true">
                <img src="@/assets/feedback.svg" class="lg:hidden" alt="意見回饋"/>
              </button>
            </div>
          </div>
          <!-- 圖片瀏覽器 -->
          <ImageViewer :specimenData="specimenData" class="flex-1 flex flex-col min-h-0"/>
        </div>
        <div class="sm:hidden lg:block">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 mt-10 sm:mt-0">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="
                  shadow
                  overflow-hidden
                  border-b border-gray-200
                  rounded-lg
                "
              >
                <!-- -----------------標本資訊表格(1024以上、640px以下顯示)--------------- -->
                <table
                  class="min-w-full divide-y divide-gray-200 table-fixed"
                  aria-describedby="Specimen Main Info"
                >
                  <!-- -----------------館藏資訊--------------- -->
                  <tbody>
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        館藏資訊
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap">
                        &emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;
                        &emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">館號</td>
                      <td class="px-3 py-2 text-left">
                        {{ specimenCatalogNumber }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        模式類型
                      </td>
                      <!-- 模式類型 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.type_status }}
                      </td>
                    </tr>
                    <!-- -----------------分類鑑定--------------- -->
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        分類鑑定
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">學名</td>
                      <!-- 學名 -->
                      <td class="px-3 py-2 text-left">
                        <span v-html="specimenData.revised_taxon" ></span>
                        &ensp;<span class="text-gray-300">{{ displayAcceptedStatus }}</span>
                        &ensp;<a class="underline text-sm" :href="'https://www.tbn.org.tw/taxasearch/result?name=' + cleanRevisedTaxonTbnString" target="_blank">TBN</a>
                        &ensp;<a class="underline text-sm" :href="'https://taicol.tw/catalogue?higherTaxa=t0000003&higherTaxa_str=Plantae%20植物界&keyword=' + cleanRevisedTaxonTaicolString" target="_blank">TaiCOL</a>
                        &ensp;<a class="underline text-sm" :href="'https://tai2.ntu.edu.tw/search/1/' + cleanRevisedTaxonTai2String" target="_blank">TAI2</a>
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">中名</td>
                      <!-- 中名 -->
                      <td class="px-3 py-2 text-left">{{ specimenData.revised_taxon_tw }}</td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">科名</td>
                      <!-- 科名 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.family }}&nbsp;{{ specimenData.familyTw }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        鑑定者
                      </td>
                      <!-- 鑑定者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.identified_by }}
                      </td>
                    </tr>
                    <!-- -----------------採集資訊--------------- -->
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        採集資訊
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集者
                      </td>
                      <!-- 採集者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.recorded_by }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        同行者
                      </td>
                      <!-- 同行者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.accompanied_by }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集編號
                      </td>
                      <!-- 採集編號 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.record_number }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集日期
                      </td>
                      <!-- 採集日期 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.event_date_day }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        當地原生性
                      </td>
                      <!-- 當地原生性 -->
                      <td class="px-3 py-2 text-left">
                        {{ establishmentMeans(specimenData.establishment_means) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">物候</td>
                      <!-- 物候 -->
                      <td class="px-3 py-2 text-left">{{ specimenData.life_stage_reproductive_condition }}</td>
                    </tr>

                    <!-- 性別 -->
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">性別</td>
                      <td class="px-3 py-2 text-left">{{ sexDescription(specimenData.sex) }}</td>
                    </tr>

                    <!-- 交換來源 -->
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">交換來源</td>
                      <td class="px-3 py-2 text-left">{{ specimenData.specimen_source }}</td>
                    </tr>

                    <!-- -----------------地理資訊--------------- -->
                    <tr class="bg-gray-200">
                      <th
                        scope="col"
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        地理資訊
                      </th>
                      <th scope="col" />
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">國家</td>
                      <!-- 國家 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.country }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        行政區
                      </td>
                      <!-- 行政區 -->
                      <td class="px-3 py-2 text-left">
                        <span v-if="specimenData.state_tw !== null"
                          >{{ specimenData.state_tw }}&nbsp;</span
                        >{{ specimenData.county }}&nbsp;{{
                          specimenData.township
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集地點
                      </td>
                      <!-- 採集地點 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.verbatim_locality_chinese }}
                        &nbsp;
                        {{ specimenData.location_remarks_chinese }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        海拔高度(m)
                      </td>
                      <!-- 海拔高度 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.elevation }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        GPS座標
                      </td>
                      <!-- GPS座標 -->
                      <td class="px-3 py-2 text-left">
                        <div class="flex space-x-3 items-center">
                          <span>
                            {{ specimenData.gmap_latitude }},&nbsp;
                            {{ specimenData.gmap_longitude }}&nbsp;
                          </span>
                          <div v-if="specimenData.is_sensitive" data-bs-toggle="tooltip" :title="eyeOffTooltip">
                            <EyeOffIcon class="h-5 w-5 icon" aria-hidden="true" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- map -->
                <SpecimenMap
                  style="z-index: 0; height: 40vh"
                  name="map1"
                  :lat="specimenData.gmap_latitude"
                  :lng="specimenData.gmap_longitude"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="hidden sm:block lg:hidden">
        <div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-5 md:gap-10 mt-20">
          <!-- -----------------標本資訊表格(640~1024px顯示)--------------- -->
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="
                  shadow
                  overflow-hidden
                  border-b border-gray-200
                  sm:rounded-lg
                "
              >
                <table
                  class="min-w-full divide-y divide-gray-200 table-fixed"
                  aria-describedby="Specimen Main Info"
                >
                  <thead style='display:none;'>
                    <tr>
                      <th id="item">Item</th>
                      <th id="data">Data</th>
                    </tr>
                  </thead>
                  <!-- -----------------館藏資訊--------------- -->
                  <tbody>
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        館藏資訊
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">館號</td>
                      <td class="px-3 py-2 text-left">
                        {{ specimenCatalogNumber }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        模式類型
                      </td>
                      <!-- 模式類型 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.type_status }}
                      </td>
                    </tr>
                    <!-- -----------------分類鑑定--------------- -->
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        分類鑑定
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">學名</td>
                      <!-- 學名 -->
                      <td v-html="specimenData.revised_taxon" class="px-3 py-2 text-left"/>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">中名</td>
                      <!-- 中名 -->
                      <td class="px-3 py-2 text-left">{{ specimenData.revised_taxon_tw }}</td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">科名</td>
                      <!-- 科名 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.family }}&nbsp;{{ specimenData.familyTw }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        鑑定者
                      </td>
                      <!-- 鑑定者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.identified_by }}
                      </td>
                    </tr>
                    <!-- -----------------採集資訊--------------- -->
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        採集資訊
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集者
                      </td>
                      <!-- 採集者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.recorded_by }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        同行者
                      </td>
                      <!-- 同行者 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.accompanied_by }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集編號
                      </td>
                      <!-- 採集編號 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.record_number }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集日期
                      </td>
                      <!-- 採集日期 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.event_date_day }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="
                  shadow
                  overflow-hidden
                  border-b border-gray-200
                  sm:rounded-lg
                "
              >
                <table
                  class="min-w-full divide-y divide-gray-200 table-fixed"
                  aria-describedby="Record Info"
                >
                  <!-- -----------------採集資訊--------------- -->
                  <tbody>
                    <tr class="bg-gray-200">
                      <td
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        採集資訊
                      </td>
                      <td/>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        當地原生性
                      </td>
                      <!-- 當地原生性 -->
                      <td class="px-3 py-2 text-left">
                        {{ establishmentMeans(specimenData.establishment_means) }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">物候</td>
                      <!-- 物候 -->
                      <td class="px-3 py-2 text-left">{{ specimenData.life_stage_reproductive_condition }}</td>
                    </tr>

                    <!-- 性別 -->
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">性別</td>
                      <td class="px-3 py-2 text-left">{{ sexDescription(specimenData.sex) }}</td>
                    </tr>

                    <!-- 交換來源 -->
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">交換來源</td>
                      <td class="px-3 py-2 text-left">{{ specimenData.specimen_source }}</td>
                    </tr>

                    <!-- -----------------地理資訊--------------- -->
                    <tr class="bg-gray-200">
                      <th
                        scope="col"
                        class="
                          px-3
                          py-2
                          text-left
                          font-bold
                          text-gray-500
                          whitespace-nowrap
                          w-36
                        "
                      >
                        地理資訊
                      </th>
                      <th scope="col" />
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">國家</td>
                      <!-- 國家 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.country }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        行政區
                      </td>
                      <!-- 行政區 -->
                      <td class="px-3 py-2 text-left">
                        <span v-if="specimenData.state_tw !== null"
                          >{{ specimenData.state_tw }}&nbsp;</span
                        >{{ specimenData.county }}&nbsp;{{
                          specimenData.township
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        採集地點
                      </td>
                      <!-- 採集地點 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.verbatim_locality_chinese }}
                        &nbsp;
                        {{ specimenData.location_remarks_chinese }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        海拔高度(m)
                      </td>
                      <!-- 海拔高度 -->
                      <td class="px-3 py-2 text-left">
                        {{ specimenData.elevation }}
                      </td>
                    </tr>
                    <tr>
                      <td class="px-3 py-2 whitespace-nowrap text-left">
                        GPS座標
                      </td>
                      <!-- GPS座標 -->
                      <td class="px-3 py-2 text-left">
                        <div class="flex space-x-3 items-center">
                          <span>
                            {{ specimenData.gmap_latitude }},&nbsp;
                            {{ specimenData.gmap_longitude }}&nbsp;
                          </span>
                          <div v-if="specimenData.is_sensitive" data-bs-toggle="tooltip" :title="eyeOffTooltip">
                            <EyeOffIcon class="h-5 w-5 icon" aria-hidden="true" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- map -->
                <SpecimenMap
                  style="z-index: 0; height: 20vh"
                  name="map2"
                  :lat="specimenData.gmap_latitude"
                  :lng="specimenData.gmap_longitude"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- identification history -->
      <IdentificationHistory :surveyDataId="this.specimenData.survey_data_id" />
    </div>
  </div>
  <!-- 意見回饋視窗 -->
  <transition name="slide-fade">
    <FeedbackDialog
      v-if="feedbackShow"
      @close="closeDialog"
      class="feedbackDiolog z-50 w-full"
      :specimenId="this.$route.params.id"
    />
  </transition>
  <div
    v-if="feedbackShow"
    class="fixed top-0 left-0 z-40 bg-gray-500 opacity-50 w-full h-full"
  ></div>
  <div class="footer-bg"></div>
</template>

<script>
import AuthMixins from '@/mixins/auth';
import { downloadSpecimenImage, getSpecimenNumberPrefix, getSpecimenById, getSpecimenByCatalogNumber } from '@/api/specimen';
import ImageViewer from "@/views/search/ImageViewer.vue";
import FeedbackDialog from "@/views/search/FeedbackDialog.vue";
import IdentificationHistory from "@/views/search/IdentificationHistory.vue";
import Utils from "@/utils/index";
import SpecimenMap from "@/views/search/SpecimenMap.vue";
import { EyeOffIcon } from "@heroicons/vue/solid";

export default {
  mixins: [AuthMixins],
  components: {
    ImageViewer,
    FeedbackDialog,
    IdentificationHistory,
    SpecimenMap,
    EyeOffIcon
  },

  data() {
    return {
      specimenData: null,
      feedbackShow: false,
      specimenNumberPrefix: "",
      specimenCatalogNumber: "",
    };
  },

  computed: {
    // 移除 HTML tag
    cleanRevisedTaxonTbnString() {
      return this.specimenData.revised_taxon_for_tbn.replace(/<\/?[^>]+(>|$)/g, "");
    },
    // 移除 HTML tag
    cleanRevisedTaxonTaicolString() {
      return this.specimenData.revised_taxon_for_taicol.replace(/<\/?[^>]+(>|$)/g, "");
    },
    cleanRevisedTaxonTai2String() {
      return this.specimenData.revised_taxon_for_tai2.replace(/<\/?[^>]+(>|$)/g, "");
    },
    // 工具提示框
    eyeOffTooltip() {
      if (this.specimenData && this.specimenData.coordinate_masking_type === 1) {
        return '屏蔽至小數點下一位';
      }
      if (this.specimenData && this.specimenData.coordinate_masking_type === 2) {
        return '屏蔽至小數點下兩位';
      }

      return '';
    },
    // 顯示接受名狀態
    displayAcceptedStatus() {
      if (this.specimenData && this.specimenData.accepted_status === 'a') {
        return '[接受名]';
      }
      if (this.specimenData && this.specimenData.accepted_status === 'h') {
        return '[未決學名]';
      }
      return '[非接受名]'
    }
  },

  async mounted() {
    try {
      // 取得館號前綴
      const response = await getSpecimenNumberPrefix()
      if (response.data) {
        this.specimenNumberPrefix = response.data.prefix
      }

      // 透過 id 取得標本資料
      if (this.$route.params && this.$route.params.id !== undefined) {
        const response = await getSpecimenById(this.$route.params.id, this.loggedIn)
        this.specimenData = response.data

        // 透過 query 方法取得指定館號的標本資料
      } else if ((this.$route.params && this.$route.params.number !== undefined) ||
        (this.$route.query && this.$route.query.catalognum !== undefined)) {
        // 解析 catalognum 值
        const pattern = `(?:(${this.specimenNumberPrefix})?)(\\d+)`
        const regex = new RegExp(pattern, 'i')

        var matched = null
        if (this.$route.params && this.$route.params.number !== undefined) {
          matched = this.$route.params.number.match(regex)
        } else if (this.$route.query && this.$route.query.catalognum !== undefined) {
          matched = this.$route.query.catalognum.match(regex)
        }

        // 確認有館號
        if (matched && matched[2]) {
          // 根據館號取得標本資料
          const catalogNumber = Number(matched[2])
          const response = await getSpecimenByCatalogNumber(catalogNumber, this.loggedIn)
          if (response.data) {
            this.specimenData = response.data
          }
        } else {
          throw new Error('invalid query string')
        }
      } else {
        throw new Error('the query method is not available')
      }

      // 館號前補 0 至六位數
      this.specimenCatalogNumber = this.specimenNumberPrefix + Utils.pad(this.specimenData.catalog_number, 6)

      setTimeout(() => {
        document.getElementById('scroll-position').scrollIntoView({ behavior: 'smooth' });
      }, 100)
    } catch (error) {
      console.log("err", error);
    }
  },

  methods: {
    establishmentMeans(code) {
      if (code === "a01") {
        return "野生";
      } else if (code === "b01") {
        return "栽培";
      } else {
        return "不確定";
      }
    },

    sexDescription(code) {
      if (!code) {
        return ''
      }

      const sexLabels = [
        { key: 'a', value: '雌花' },
        { key: 'b', value: '雄花' },
        { key: 'o', value: '兩性花' },
        { key: 't', value: '無性花' },
        { key: 'u', value: '不確定' }
      ]

      return code.split(';')
        .map(key => sexLabels.find(label => label.key === key)?.value || '')
        .join(';')
    },

    backToSearchResultPage() {
      this.$router.push("/search");
    },

    closeDialog() {
      this.feedbackShow = false;
    },

    // 下載標本原圖
    downloadImage (id) {
      downloadSpecimenImage(id).then(resp => {
        const filename = this.specimenCatalogNumber + '.' + resp.type.split('/').pop()
        Utils.fileDownload(resp, filename)
        console.log("Download Specimen Image succeed.");
      }).catch(() => {
        console.log('Download Specimen Image failed')
      })
    },
  },
};
</script>

<style>
.loading {
  @apply text-2xl text-center my-64;
}

.web-bg-color {
  background-color: #64859d;
}

.web-dark-bg-color {
  background-color: #405d73;
}

.feedback-button {
  @apply web-bg-color rounded px-5 py-2 text-white;
}

.feedback-button:hover {
  @apply web-dark-bg-color rounded px-5 py-2 text-white;
}

.map {
  height: 150px;
}

.feedbackDiolog {
  position: fixed;
  top: 50%;
  left: 0px;
  margin-top: -310px;
}

.slide-fade-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter-from {
  transform: translateY(-5%);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-5%);
  opacity: 0;
}
</style>
